import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { X, Copy, ChevronDown } from "lucide-react";
import Button from "../../../Shared/Buttons/Button";
import Input from "../../../Shared/Input";
import Label from "../../../Shared/Label";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../Shared/Select";
import { toast } from "react-toastify";
import { UPDATEPRICEDEFINITIONSEXTERNAL } from "../../../../Utils/API/apiHelper";
import ApiHelper from "../../../../Utils/API/apiHelper";

export default function PriceAPIDialog({ dialogOpen, closeDialog, userCompanies, ...props }) {
  const classes = useStyles();
  const appSettings = ApiHelper.appSettings;

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [apiUrl, setApiUrl] = useState("");

  // Console.log userCompanies when they change
  useEffect(() => {
    if (userCompanies) {
      // Set the first company as default if available
      if (userCompanies.length > 0) {
        setSelectedCompany(userCompanies[0]);
      }
    }
  }, [userCompanies]);

  // Generate API URL when company changes
  useEffect(() => {
    if (selectedCompany) {
      // Use the same base URL pattern as ModifyExternalApi
      const generatedUrl = `${appSettings.TAPLR_API_BASE_URL}/${UPDATEPRICEDEFINITIONSEXTERNAL}${selectedCompany.uuid}`;
      setApiUrl(generatedUrl);
    }
  }, [selectedCompany]);

  const exampleJSON = `{
  "source": "SAP",
  "updates": [
    {
      "code": "001", // The code of the price definition you want to update
      "rate": 0.27,
      "validFrom": "01/06/2025 8:30:00",
      "unit": "kWh"
    },
    {
      "code": "002",
      "rate": 21.00,
      "unit": "hour"
    }
  ]
}`;

  const handleCloseDialog = () => {
    closeDialog();
  };

  const handleCopyJSON = async () => {
    try {
      await navigator.clipboard.writeText(exampleJSON);
      toast.success("JSON copied to clipboard");
    } catch (error) {
      console.error("Failed to copy JSON:", error);
      toast.error("Failed to copy JSON");
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(apiUrl);
      toast.success("API URL copied to clipboard");
    } catch (error) {
      console.error("Failed to copy URL:", error);
      toast.error("Failed to copy URL");
    }
  };

  return (
    <Dialog className={classes.dialog} open={dialogOpen} aria-labelledby="price-api-dialog-title" classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title} id="price-api-dialog-title">
          Price API
        </DialogTitle>
        <button onClick={() => handleCloseDialog()} className={classes.dialog__closeButton}>
          <X size={19} />
        </button>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          
          <p className={classes.explanationText}>
            You can integrate the Cost API into any software or system that can send pricing data, making it easy to automate price updates from your existing tools.
          </p>

          {/* Company Selection */}
          <div className={classes.sectionContainer}>
            <Label>Select Company</Label>
            <Select 
              value={selectedCompany?.id} 
              onChange={setSelectedCompany} 
              placeholder="Select a company"
              fullwidth
            >
              <SelectTrigger>
                {selectedCompany ? selectedCompany.name : "Select a company"}
              </SelectTrigger>
              <SelectContent>
                {userCompanies && userCompanies.map((company) => (
                  <SelectItem key={company.id} value={company}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* API URL */}
          {selectedCompany && (
            <div className={classes.sectionContainer}>
              <Label>API Endpoint</Label>
              <div className={classes.urlContainer}>
                <Input
                  value={apiUrl}
                  fullWidth
                  readOnly
                  style={{ width: "100%" }}
                  placeholder="API URL will appear here"
                />
                <Button 
                  size="medium" 
                  variant="secondary" 
                  startIcon={<Copy />}
                  onClick={handleCopyUrl}
                >
                  Copy
                </Button>
              </div>
            </div>
          )}

          {/* JSON Example */}
          <div className={classes.sectionContainer}>
            <Label>Example JSON Payload</Label>
            <div className={classes.codeContainer}>
              <div className={classes.codeHeader}>
                <span className={classes.codeLabel}>json</span>
                <Button 
                  size="extraSmall" 
                  variant="secondary" 
                  startIcon={<Copy />}
                  onClick={handleCopyJSON}
                >
                  Copy
                </Button>
              </div>

              <pre className={classes.codeBlock}>
                <code>{exampleJSON}</code>
              </pre>
            </div>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}

const useStyles = makeStyles(() => ({
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    width: "520px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",

    padding: "0px !important",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 16px 0px",
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    // remove scrollbar
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
  },

  explanationText: {
    fontSize: "16px",
    color: "#0C0E29",
    lineHeight: "1.5",
    margin: "0 0 20px 0",
  },

  sectionContainer: {
    width: "100%",
    marginBottom: "16px",
  },

  formControl: {
    width: "100%",
  },

  select: {
    fontSize: "14px",
  },

  urlContainer: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    width: "100%",
  },

  copyButton: {
    flexShrink: 0,
  },

  codeHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: 8,
    paddingBottom: 0,
  },

  codeLabel: {
    fontSize: "14px",
    color: "#0C0E29",
    opacity: ".8",
    fontWeight: "500",
  },

  codeContainer: {
    width: "100%",
    backgroundColor: "#F8F8F8",
    borderRadius: "4px",
    padding: 0,
  },

  codeBlock: {
    margin: 0,
    padding: 16,
    fontSize: "14px",
    fontFamily: "Monaco, 'Courier New', monospace",
    color: "#0C0E29",
    backgroundColor: "transparent",
    overflow: "auto",
    overflowY: "hidden",
    lineHeight: "1.4",
    whiteSpace: "pre",
    
    "& code": {
      fontFamily: "inherit",
    },

    // Custom scrollbar styles
    "&::-webkit-scrollbar": {
      height: "4px",
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#DCDCDC",
      borderRadius: "2px",
      
      "&:hover": {
        backgroundColor: "#BDBDBD",
      },
    },

    "&::-webkit-scrollbar-corner": {
      backgroundColor: "transparent",
    },

    // Remove scrollbar arrows
    "&::-webkit-scrollbar-button": {
      display: "none",
    },
  },
}));
